import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  HideEntityMutation,
  HideEntityMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { HiddenEntityType } from "lib/types/graphQLEnums";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateCachedNoticeHidden } from "./utils";

const hideEntityMutation = graphql(`
  mutation hideEntity($input: HiddenEntityInput!) {
    hideEntity(input: $input) {
      entityType
      entityId
      isHidden
    }
  }
`);

export type NoticeVisibilityTarget = {
  guid: string;
  procurementStage: { id: string; stage: string };
  score: number | null | undefined;
  signalTypes: Record<string, string[]> | undefined;
};

/**
 * Hides a notice for the current user's team. Returns `hideNotice`, which hides the given notice.
 */
export function useHideNotice(
  notice: NoticeVisibilityTarget,
  contextSource: string,
  options?: UseMutationOptions<HideEntityMutation, ClientError, HideEntityMutationVariables>,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  const mutation = useGraphQlMutation(hideEntityMutation, {
    ...options,
    onSuccess: (data, variables, context) => {
      // A hide on any stage other than a tender is labelled pre-engage.
      const isPreEngage = notice.procurementStage.stage.toLowerCase() !== "tender";
      logEvent(EventNames.qualificationActioned, {
        "Record GUID": notice.guid,
        "Context source": contextSource,
        "Qualification label": isPreEngage ? "Pre Engage Not Relevant" : "Not Relevant",
        Stage: notice.procurementStage.stage,
        "Signal types": notice.signalTypes ? Object.keys(notice.signalTypes) : [],
        "Signal score": notice.score,
        Signals: notice.signalTypes,
      });

      updateCachedNoticeHidden(queryClient, notice, true);

      options?.onSuccess?.(data, variables, context);
    },
  });

  return {
    ...mutation,
    hideNotice: () =>
      mutation.mutate({
        input: { entityType: HiddenEntityType.Notice, entityId: notice.procurementStage.id },
      }),
  };
}
